import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  FileSpreadsheet, 
  FileText, 
  BarChart3,
  Users,
  Calendar,
  MapPin,
  Phone,
  User,
  Download,
  Eye,
  RefreshCw,
  TrendingUp,
  Settings2,
  CheckSquare
} from 'lucide-react';

// Simple chart components
const BarChartSimple = ({ data, title }) => {
  const maxValue = Math.max(...Object.values(data), 1);
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 10);
  
  return (
    <div className="space-y-2">
      <h4 className="font-medium text-sm text-slate-700">{title}</h4>
      <div className="space-y-1">
        {entries.map(([label, value]) => (
          <div key={label} className="flex items-center gap-2">
            <span className="text-xs text-slate-500 w-24 truncate" title={label}>{label}</span>
            <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
              <div 
                className="h-full bg-navy rounded transition-all duration-500"
                style={{ width: `${(value / maxValue) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium w-8 text-right">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PieChartSimple = ({ data, title }) => {
  const total = Object.values(data).reduce((sum, v) => sum + v, 0);
  const colors = ['#1a365d', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'];
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  
  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-slate-700">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {entries.map(([label, value], idx) => (
          <div key={label} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors[idx % colors.length] }} />
            <span className="text-xs">{label}: {value} ({((value/total)*100).toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LineChartSimple = ({ data, title }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d[1]), 1);
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1 || 1)) * 100,
    y: 100 - (d[1] / maxValue) * 80,
    date: d[0],
    value: d[1]
  }));
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  return (
    <div className="space-y-2">
      <h4 className="font-medium text-sm text-slate-700">{title}</h4>
      <div className="h-32 relative">
        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
          <path d={pathD} fill="none" stroke="#1a365d" strokeWidth="2" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="2" fill="#1a365d" />
          ))}
        </svg>
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span>{data[0]?.[0]}</span>
        <span>{data[data.length-1]?.[0]}</span>
      </div>
    </div>
  );
};

export default function FormResultsPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { api, isSuperAdmin, isAdmin } = useAuth();
  
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchIts, setSearchIts] = useState('');
  const [filterMohalla, setFilterMohalla] = useState('');
  const [filterSector, setFilterSector] = useState('');
  
  // Submission detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportFields, setExportFields] = useState({ auto_fields: [], form_fields: [] });
  const [selectedAutoFields, setSelectedAutoFields] = useState([]);
  const [selectedFormFields, setSelectedFormFields] = useState([]);
  const [loadingExportFields, setLoadingExportFields] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (searchIts) params.append('its', searchIts);
      if (filterMohalla) params.append('mohalla', filterMohalla);
      if (filterSector) params.append('sector', filterSector);
      
      const response = await api.get(`/forms/${formId}/submissions?${params}`);
      setSubmissions(response.data.submissions || []);
      setForm(response.data.form);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [api, formId, page, searchIts, filterMohalla, filterSector]);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const response = await api.get(`/forms/${formId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  }, [api, formId]);

  useEffect(() => {
    fetchSubmissions();
    fetchAnalytics();
  }, [fetchSubmissions, fetchAnalytics]);

  const openExportDialog = async (format) => {
    setExportFormat(format);
    setShowExportDialog(true);
    setLoadingExportFields(true);
    try {
      const response = await api.get(`/forms/${formId}/export-fields`);
      const data = response.data;
      setExportFields(data);
      // Select all fields by default
      setSelectedAutoFields(data.auto_fields.map(f => f.key));
      setSelectedFormFields(data.form_fields.map(f => f.key));
    } catch (error) {
      toast.error('Failed to load export fields');
    } finally {
      setLoadingExportFields(false);
    }
  };

  const toggleAutoField = (key) => {
    setSelectedAutoFields(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleFormField = (key) => {
    setSelectedFormFields(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      toast.info(`Generating ${exportFormat.toUpperCase()} export...`);
      
      const params = new URLSearchParams({ format: exportFormat });
      if (selectedAutoFields.length > 0 && selectedAutoFields.length < exportFields.auto_fields.length) {
        params.append('fields', selectedAutoFields.join(','));
      }
      if (selectedFormFields.length > 0 && selectedFormFields.length < exportFields.form_fields.length) {
        params.append('form_fields', selectedFormFields.join(','));
      }
      
      const response = await api.get(`/forms/${formId}/export?${params}`, {
        responseType: 'blob'
      });
      
      const mimeType = exportFormat === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      
      const ext = exportFormat === 'excel' ? 'xlsx' : 'pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${form?.name?.replace(/\s+/g, '_') || 'form'}_submissions.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported as ${exportFormat.toUpperCase()}`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const viewSubmissionDetail = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-10" data-testid="form-results-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(`/form-builder/${formId}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-heading font-semibold text-slate-800">
              {form?.name || 'Form Results'}
            </h1>
            <p className="text-sm text-slate-500">{total} submissions</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              fetchSubmissions();
              fetchAnalytics();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => openExportDialog('excel')} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => openExportDialog('pdf')} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="submissions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="submissions" className="gap-2">
            <Users className="w-4 h-4" />
            Submissions
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Submissions Tab */}
        <TabsContent value="submissions" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by ITS..."
                value={searchIts}
                onChange={(e) => {
                  setSearchIts(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Input
              placeholder="Filter by Mohalla..."
              value={filterMohalla}
              onChange={(e) => {
                setFilterMohalla(e.target.value);
                setPage(1);
              }}
              className="w-40"
            />
            <Input
              placeholder="Filter by Sector..."
              value={filterSector}
              onChange={(e) => {
                setFilterSector(e.target.value);
                setPage(1);
              }}
              className="w-40"
            />
          </div>

          {/* Submissions Table */}
          {submissions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No submissions found</h3>
                <p className="text-slate-500 mt-1">Try adjusting your filters</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">ITS</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Mobile</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Mohalla</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Sector</th>
                        {/* Show form field headers */}
                        {form?.fields?.filter(f => !['its_number', 'hof_its_number', 'photo'].includes(f.type)).slice(0, 3).map(field => (
                          <th key={field.id} className="px-4 py-3 text-left text-xs font-medium text-slate-500 max-w-[120px]">
                            <span className="line-clamp-2">{field.label}</span>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Submitted</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {submissions.map((sub, idx) => (
                        <tr key={sub.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-500">{(page-1)*25 + idx + 1}</td>
                          <td className="px-4 py-3 text-sm font-mono">{sub.its_id || '-'}</td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {sub.member_data?.full_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {sub.member_data?.mobile || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {sub.member_data?.mohalla || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {sub.member_data?.sector || '-'}
                          </td>
                          {/* Show form field values */}
                          {form?.fields?.filter(f => !['its_number', 'hof_its_number', 'photo'].includes(f.type)).slice(0, 3).map(field => {
                            const value = sub.responses?.[field.id];
                            
                            // Rating field display
                            if (field.type === 'rating' && value) {
                              return (
                                <td key={field.id} className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-navy text-white text-sm font-medium">
                                      {value}
                                    </span>
                                    <span className="text-xs text-slate-400">/ {field.rating_config?.max || 5}</span>
                                  </div>
                                </td>
                              );
                            }
                            
                            // Default display
                            const displayValue = Array.isArray(value) ? value.join(', ') : String(value || '-');
                            return (
                              <td key={field.id} className="px-4 py-3 text-sm text-slate-600 max-w-[120px] truncate" title={displayValue}>
                                {displayValue}
                              </td>
                            );
                          })}
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {formatDate(sub.submitted_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => viewSubmissionDetail(sub)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <span className="text-sm text-slate-500">
                      Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {loadingAnalytics ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-4 border-navy border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !analytics ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No analytics data</h3>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-navy" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-navy">{analytics.total_submissions}</p>
                        <p className="text-sm text-slate-500">Total Submissions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">
                          {Object.keys(analytics.by_mohalla || {}).length}
                        </p>
                        <p className="text-sm text-slate-500">Mohallas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-600">
                          {Object.keys(analytics.by_sector || {}).length}
                        </p>
                        <p className="text-sm text-slate-500">Sectors</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-600">
                          {(analytics.by_date || []).length}
                        </p>
                        <p className="text-sm text-slate-500">Days Active</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Submissions by Mohalla</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChartSimple data={analytics.by_mohalla || {}} title="" />
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Submissions by Sector</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChartSimple data={analytics.by_sector || {}} title="" />
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Submissions Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <LineChartSimple data={analytics.by_date || []} title="" />
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Gender Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PieChartSimple data={analytics.by_gender || {}} title="" />
                  </CardContent>
                </Card>
              </div>
              
              {/* Field-Specific Analytics */}
              {analytics.field_analytics && Object.keys(analytics.field_analytics).length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-slate-800">Field Analytics</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {Object.entries(analytics.field_analytics).map(([fieldId, fieldData]) => (
                      <Card key={fieldId}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            {fieldData.label}
                            <Badge variant="outline" className="text-xs">
                              {fieldData.type === 'rating' ? 'Rating' : fieldData.type === 'dropdown' ? 'Dropdown' : 'Checkbox'}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {fieldData.type === 'rating' ? (
                            <div className="space-y-4">
                              {/* Rating Summary */}
                              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div className="text-center">
                                  <p className="text-3xl font-bold text-navy">{fieldData.average || '-'}</p>
                                  <p className="text-xs text-slate-500">Average Rating</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xl font-semibold text-slate-600">{fieldData.count || 0}</p>
                                  <p className="text-xs text-slate-500">Responses</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm text-slate-600">
                                    {fieldData.config?.left_label || 'Min'} → {fieldData.config?.right_label || 'Max'}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    Scale: {fieldData.config?.min || 1} - {fieldData.config?.max || 5}
                                  </p>
                                </div>
                              </div>
                              {/* Rating Distribution */}
                              {fieldData.distribution && Object.keys(fieldData.distribution).length > 0 && (
                                <BarChartSimple data={fieldData.distribution} title="Rating Distribution" />
                              )}
                            </div>
                          ) : (
                            /* Dropdown/Checkbox Distribution */
                            fieldData.distribution && Object.keys(fieldData.distribution).length > 0 ? (
                              <BarChartSimple data={fieldData.distribution} title="" />
                            ) : (
                              <p className="text-sm text-slate-400 text-center py-4">No responses yet</p>
                            )
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Submission Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
            <DialogDescription>
              ID: {selectedSubmission?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4 py-4">
              {/* Member Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Member Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">ITS:</span>
                    <span className="font-medium">{selectedSubmission.its_id || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium">{selectedSubmission.member_data?.full_name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Mobile:</span>
                    <span className="font-medium">{selectedSubmission.member_data?.mobile || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Mohalla:</span>
                    <span className="font-medium">{selectedSubmission.member_data?.mohalla || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Sector:</span>
                    <span className="font-medium">{selectedSubmission.member_data?.sector || '-'}</span>
                  </div>
                  {selectedSubmission.member_data?.age && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">Age:</span>
                      <span className="font-medium">{selectedSubmission.member_data.age}</span>
                    </div>
                  )}
                  {selectedSubmission.member_data?.gender && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">Gender:</span>
                      <span className="font-medium">{selectedSubmission.member_data.gender}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Form Responses */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-3">Form Responses</h4>
                <div className="space-y-3">
                  {Object.entries(selectedSubmission.responses || {}).map(([key, value]) => {
                    // Find field from form
                    const field = form?.fields?.find(f => f.id === key);
                    const label = field?.label || key;
                    const fieldType = field?.type;
                    
                    // Special display for rating fields
                    if (fieldType === 'rating' && field?.rating_config) {
                      const config = field.rating_config;
                      const ratingValues = [];
                      for (let v = config.min; v <= config.max; v += (config.step || 1)) {
                        ratingValues.push(v);
                      }
                      
                      return (
                        <div key={key} className="border-b pb-3">
                          <span className="text-xs text-slate-500">{label}</span>
                          <div className="flex items-center justify-between gap-2 mt-2">
                            <span className="text-xs text-slate-400 min-w-[60px]">{config.left_label}</span>
                            <div className="flex gap-1">
                              {ratingValues.map(v => (
                                <div
                                  key={v}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                    v === value
                                      ? 'bg-navy text-white'
                                      : 'bg-slate-100 text-slate-400'
                                  }`}
                                >
                                  {v}
                                </div>
                              ))}
                            </div>
                            <span className="text-xs text-slate-400 min-w-[60px] text-right">{config.right_label}</span>
                          </div>
                          <p className="text-center text-sm font-medium text-navy mt-1">Selected: {value}</p>
                        </div>
                      );
                    }
                    
                    // Default display for other fields
                    const displayValue = Array.isArray(value) ? value.join(', ') : String(value || '-');
                    
                    return (
                      <div key={key} className="border-b pb-2">
                        <span className="text-xs text-slate-500">{label}</span>
                        <p className="font-medium">{displayValue}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Timestamp */}
              <div className="text-xs text-slate-400 pt-2 border-t">
                Submitted: {formatDate(selectedSubmission.submitted_at)}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Field Selection Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Export as {exportFormat === 'pdf' ? 'PDF' : 'Excel'}
            </DialogTitle>
            <DialogDescription>
              Select which fields to include in your export. ITS Number and Date are always included.
            </DialogDescription>
          </DialogHeader>
          
          {loadingExportFields ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-4 border-navy border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-5 py-2 max-h-[50vh] overflow-y-auto">
              {/* Auto-Fetch Fields */}
              {exportFields.auto_fields.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700">Member Fields</h4>
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      data-testid="toggle-all-auto-fields"
                      onClick={() => {
                        if (selectedAutoFields.length === exportFields.auto_fields.length) {
                          setSelectedAutoFields([]);
                        } else {
                          setSelectedAutoFields(exportFields.auto_fields.map(f => f.key));
                        }
                      }}
                    >
                      {selectedAutoFields.length === exportFields.auto_fields.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {exportFields.auto_fields.map(field => (
                      <div key={field.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                        <Checkbox
                          id={`auto-${field.key}`}
                          checked={selectedAutoFields.includes(field.key)}
                          onCheckedChange={() => toggleAutoField(field.key)}
                          data-testid={`field-${field.key}`}
                        />
                        <Label htmlFor={`auto-${field.key}`} className="text-sm cursor-pointer">
                          {field.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Fields */}
              {exportFields.form_fields.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700">Form Fields</h4>
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      data-testid="toggle-all-form-fields"
                      onClick={() => {
                        if (selectedFormFields.length === exportFields.form_fields.length) {
                          setSelectedFormFields([]);
                        } else {
                          setSelectedFormFields(exportFields.form_fields.map(f => f.key));
                        }
                      }}
                    >
                      {selectedFormFields.length === exportFields.form_fields.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {exportFields.form_fields.map(field => (
                      <div key={field.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                        <Checkbox
                          id={`form-${field.key}`}
                          checked={selectedFormFields.includes(field.key)}
                          onCheckedChange={() => toggleFormField(field.key)}
                          data-testid={`field-${field.key}`}
                        />
                        <Label htmlFor={`form-${field.key}`} className="text-sm cursor-pointer">
                          {field.label}
                          <Badge variant="outline" className="ml-2 text-[10px]">{field.type}</Badge>
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={exporting || (selectedAutoFields.length === 0 && selectedFormFields.length === 0)}
              data-testid="confirm-export-btn"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export {exportFormat === 'pdf' ? 'PDF' : 'Excel'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
